<template>
  <div class="mt-5">
    <div class="mb-8">
      <div
        class="inline-flex items-center px-3 py-1 rounded-full bg-primary-light text-primary-dark text-xs font-semibold mb-4"
      >
        <span class="w-2 h-2 bg-primary-dark rounded-full mr-2 animate-pulse"></span>
        {{ themeBadge }}
      </div>
      <h1 class="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 leading-tight md:leading-9">
        {{ questionTitle }}
      </h1>
      <p v-if="questionSubtitle" class="mt-3 text-lg text-gray-700">{{ questionSubtitle }}</p>
    </div>

    <div class="mt-8">
      <transition-group
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 -translate-x-5"
        leave-active-class="transition-all duration-200 ease-in"
        leave-to-class="opacity-0 translate-x-5"
        tag="div"
        class="space-y-4"
      >
        <div
          v-for="(option, index) in shuffledOptions"
          :key="index"
          class="group relative flex items-start p-5 border-2 rounded-xl cursor-pointer transition-all duration-300 transform"
          :class="getOptionClasses(index)"
          @click="selectAnswer(index)"
        >
          <div class="flex items-center h-6 mr-4 mt-0.5">
            <div
              class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200"
              :class="getRadioClasses(index)"
            >
              <div
                v-if="selectedAnswer === index"
                class="w-3 h-3 rounded-full transition-all duration-200"
                :class="getRadioInnerClasses(index)"
              ></div>
            </div>
          </div>

          <label :for="`option-${index}`" class="flex-1 cursor-pointer">
            <span class="text-base font-medium leading-relaxed" :class="getTextClasses(index)">
              {{ option }}
            </span>
          </label>

          <transition enter-active-class="transition-all" enter-from-class="opacity-0" enter-to-class="opacity-100">
            <div
              v-if="showResult && checkIsCorrectAnswer(index)"
              class="ml-3 flex items-center justify-center w-8 h-8 rounded-full bg-green-100 animate-bounce"
            >
              <CheckCircleIcon class="w-6 h-6 text-green-600" />
            </div>
          </transition>
          <transition enter-active-class="transition-all" enter-from-class="opacity-0" enter-to-class="opacity-100">
            <div
              v-if="showResult && selectedAnswer === index && !checkIsCorrectAnswer(index)"
              class="ml-3 flex items-center justify-center w-8 h-8 rounded-full bg-red-100"
            >
              <XCircleIcon class="w-6 h-6 text-red-600" />
            </div>
          </transition>

          <transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
          >
            <div
              v-if="showResult && checkIsCorrectAnswer(index)"
              class="absolute -top-3 -right-3 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow-lg"
            >
              ✓ {{ t("correct") }}
            </div>
          </transition>
        </div>
      </transition-group>
    </div>

    <transition
      enter-active-class="transition-all duration-500 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
    >
      <div v-if="showResult" class="mt-8 p-6 rounded-2xl border-l-4 shadow-lg" :class="getExplanationClasses()">
        <div class="ml-4 flex-1">
          <h3 class="text-lg font-bold mb-2" :class="isCorrect ? 'text-green-900' : 'text-blue-900'">
            {{ isCorrect ? t("excellent_answer") : t("explanation") }}
          </h3>
          <MarkdownText
            class="text-sm leading-relaxed space-y-2"
            :class="isCorrect ? 'text-green-800' : 'text-blue-800'"
            :content="explanation"
          />
        </div>
      </div>
    </transition>

    <div class="mt-10 flex w-full items-center justify-between">
      <GenericButton v-if="componentStep > 1" @click="$emit('previous')" secondary class="w-auto px-6 py-2.5">
        {{ t("previous") }}
      </GenericButton>
      <div v-else></div>
      <GenericButton
        v-if="selectedAnswer !== null"
        @click="handleNext"
        :disabled="!showResult"
        class="w-auto px-6 py-2.5 transition-all duration-200"
        :class="showResult && isCorrect ? 'animate-pulse' : ''"
      >
        {{ isLastQuestion ? t("finish_quiz") : t("next") }}
      </GenericButton>
    </div>
  </div>
</template>

<script>
import { GenericButton, MarkdownText } from "@estaly/ui";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/solid";
import { useI18n } from "vue-i18n";

export default {
  components: {
    GenericButton,
    MarkdownText,
    CheckCircleIcon,
    XCircleIcon,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: {
    componentStep: {
      type: Number,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    totalQuestions: {
      type: Number,
      required: false,
    },
    themeBadge: {
      type: String,
      required: true,
    },
    questionTitle: {
      type: String,
      required: true,
    },
    questionSubtitle: {
      type: String,
      required: false,
    },
    options: {
      type: Array,
      required: true,
    },
    correctAnswer: {
      type: [Number, Boolean],
      required: true,
    },
    explanation: {
      type: String,
      required: true,
    },
  },
  emits: ["next", "previous"],
  data() {
    const indices = this.options.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return {
      selectedAnswer: null,
      showResult: false,
      shuffledIndices: indices,
    };
  },
  computed: {
    shuffledOptions() {
      return this.shuffledIndices.map((i) => this.options[i]);
    },
    shuffledCorrectAnswer() {
      return this.shuffledIndices.indexOf(this.correctAnswer);
    },
    isLastQuestion() {
      return this.componentStep === this.totalQuestions;
    },
    isCorrect() {
      return this.checkIsCorrectAnswer(this.selectedAnswer);
    },
  },
  methods: {
    checkIsCorrectAnswer(index) {
      return index === this.shuffledCorrectAnswer;
    },
    selectAnswer(index) {
      if (this.showResult) return; // User cannot change the answer after the result is shown
      this.selectedAnswer = index;
      this.showResult = true;
    },
    getOptionClasses(index) {
      if (!this.showResult) {
        return {
          "border-gray-300 bg-white hover:border-primary hover:bg-primary-light hover:shadow-md": true,
          "border-primary bg-primary-light shadow-md ring-2 ring-primary-lighter": this.selectedAnswer === index,
        };
      }

      if (this.checkIsCorrectAnswer(index)) {
        return "border-green-500 bg-linear-to-r/srgb from-green-50 shadow-lg ring-2 ring-green-200";
      }
      if (this.selectedAnswer === index && !this.checkIsCorrectAnswer(index)) {
        return "border-red-500 bg-linear-to-r/srgb from-red-50 shadow-md ring-2 ring-red-200";
      }
      return "border-gray-200 bg-gray-50 opacity-60";
    },
    getRadioClasses(index) {
      if (!this.showResult) {
        return {
          "border-gray-400 group-hover:border-primary": true,
          "border-primary bg-primary-light": this.selectedAnswer === index,
        };
      }

      if (this.checkIsCorrectAnswer(index)) {
        return "border-green-500 bg-green-100";
      }
      if (this.selectedAnswer === index && !this.checkIsCorrectAnswer(index)) {
        return "border-red-500 bg-red-100";
      }
      return "border-gray-300";
    },
    getRadioInnerClasses(index) {
      if (!this.showResult) {
        return "bg-primary";
      }

      if (this.checkIsCorrectAnswer(index)) {
        return "bg-green-600";
      }
      if (this.selectedAnswer === index && !this.checkIsCorrectAnswer(index)) {
        return "bg-red-600";
      }
      return "";
    },
    getTextClasses(index) {
      if (!this.showResult) {
        return "text-gray-900 group-hover:text-gray-900";
      }

      if (this.checkIsCorrectAnswer(index)) {
        return "text-green-900 font-semibold";
      }
      if (this.selectedAnswer === index && !this.checkIsCorrectAnswer(index)) {
        return "text-red-900";
      }
      return "text-gray-500";
    },
    getExplanationClasses() {
      return this.isCorrect
        ? "bg-linear-to-r/srgb from-green-50 border-green-400"
        : "bg-linear-to-r/srgb from-blue-50 border-blue-400";
    },
    handleNext() {
      if (this.showResult) {
        const originalIndex = this.shuffledIndices[this.selectedAnswer];
        this.$emit("next", { isCorrect: this.isCorrect, selectedAnswer: originalIndex });
      }
    },
  },
};
</script>

<i18n src="./Step.i18n.json"></i18n>
